import React, { useState } from 'react'
import useSWR from 'swr'
import { fetcher } from '../utils/api'
import { BarChart3, TrendingUp, Users, Calendar, RefreshCw } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import PageHeader from '../components/admin/PageHeader'
import StatCard from '../components/admin/StatCard'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']

export default function Analytics() {
  const [days, setDays] = useState(30)
  const [weeks, setWeeks] = useState(12)

  const { data: engagement, isLoading: loadingEngagement, mutate: refreshEngagement } = useSWR(`/api/admin/analytics/engagement?days=${days}`, fetcher)
  const { data: retention, isLoading: loadingRetention } = useSWR(`/api/admin/analytics/retention?weeks=${weeks}`, fetcher)
  const { data: teamAnalytics, isLoading: loadingTeams } = useSWR('/api/admin/analytics/teams', fetcher)

  const isLoading = loadingEngagement || loadingRetention || loadingTeams

  return (
    <div className="space-y-6">
      <PageHeader title="Analytics Dashboard" description="Deep dive into user engagement and retention">
        <Select value={String(days)} onValueChange={(val) => setDays(Number(val))}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={() => refreshEngagement()}><RefreshCw className="w-4 h-4" /></Button>
      </PageHeader>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title="Total Users" value={retention?.overall?.total_users || 0} icon={Users} color="indigo" />
            <StatCard title="7-Day Retention" value={`${retention?.overall?.retention_7d || 0}%`} icon={TrendingUp} color="emerald" />
            <StatCard title="30-Day Retention" value={`${retention?.overall?.retention_30d || 0}%`} icon={Calendar} color="orange" />
            <StatCard title="Total Teams" value={teamAnalytics?.total_teams || 0} icon={BarChart3} color="purple" />
          </div>

          <Card>
            <CardHeader><CardTitle>Daily Active Users</CardTitle></CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={engagement?.daily_active_users || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Events Created</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={engagement?.events_created || []}>
                      <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis /><Tooltip />
                      <Bar dataKey="value" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Messages Sent</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={engagement?.messages || []}>
                      <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis /><Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Team Size Distribution', data: teamAnalytics?.size_distribution },
              { title: 'Plan Distribution', data: teamAnalytics?.plan_distribution },
            ].map(({ title, data: pieData }) => (
              <Card key={title}>
                <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData || []} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {pieData?.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card>
              <CardHeader><CardTitle>Sports Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={teamAnalytics?.sports_distribution || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="sport" width={80} tick={{ fontSize: 11 }} /><Tooltip />
                      <Bar dataKey="count" fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Weekly Retention Cohorts</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Week</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Signups</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Retained</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Retention Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {retention?.cohorts?.slice(-8).map((cohort, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900">{cohort.week}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{cohort.signups}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{cohort.retained}</td>
                        <td className="px-4 py-2">
                          <span className={`text-sm font-medium ${cohort.retention_rate >= 50 ? 'text-emerald-600' : cohort.retention_rate >= 25 ? 'text-amber-600' : 'text-rose-500'}`}>{cohort.retention_rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
